/*
  Back to top button.

  The button stays out of sight until the page has been scrolled past a set
  distance, then slides in. Pressing it eases the page back to the top over a
  set time, or jumps straight there when the visitor has asked for reduced
  motion.

  While it is hidden the button is also inert, so it can be neither clicked nor
  reached with the keyboard. Every number it uses is read from data attributes
  written from the theme settings, so nothing about its timing is decided here.
*/

(() => {
  // The file is requested once per page, but guard against a second copy
  // registering its listeners twice.
  if (window.kvBackToTopReady) return;
  window.kvBackToTopReady = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Starts slowly, speeds up through the middle and settles gently at the end.
  const swing = (progress) => 0.5 - Math.cos(progress * Math.PI) / 2;

  // Anything the visitor does to move the page takes over from the animation.
  const INTERRUPTING_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

  // How far the page may sit from where the animation last put it before the
  // difference counts as the visitor scrolling. Leaves room for sub-pixel
  // rounding on high-density screens.
  const SCROLL_DRIFT_TOLERANCE = 2;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

  /* Whether focus arrived from the keyboard. Engines that do not know the
     selector throw on it, so those fall back to the class the theme's own focus
     script sets for keyboard focus. */
  const hasKeyboardFocus = (element) => {
    try {
      return element.matches(':focus-visible');
    } catch (error) {
      return element.classList.contains('focused');
    }
  };

  class KvBackToTop {
    constructor(button) {
      this.button = button;
      this.threshold = Number(button.dataset.threshold) || 0;
      this.duration = Number(button.dataset.duration) || 0;
      this.position = button.dataset.position === 'left' ? 'left' : 'right';
      this.keepClear = button.dataset.keepClear || '';
      this.keepClearGap = Number(button.dataset.keepClearGap) || 0;

      this.visible = null;
      this.lift = 0;
      this.frame = 0;
      this.scrollFrame = 0;
      this.expectedScroll = 0;
      this.keyboardFocus = false;

      this.schedule = this.schedule.bind(this);
      this.update = this.update.bind(this);
      this.cancelScroll = this.cancelScroll.bind(this);

      button.addEventListener('click', () => this.scrollToTop());

      // Recorded at the moment focus arrives, because by the time the button
      // hides the pseudo-class may no longer say how it was reached.
      button.addEventListener('focus', () => {
        this.keyboardFocus = hasKeyboardFocus(button);
      });
      button.addEventListener('blur', () => {
        this.keyboardFocus = false;
      });

      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule, { passive: true });

      // A page restored from the back-forward cache, or a tab brought to the
      // front, can already be scrolled without a scroll event ever reaching it.
      window.addEventListener('pageshow', this.schedule);
      document.addEventListener('visibilitychange', this.schedule);

      // Content that finishes loading can move what the button keeps clear of
      // without any scroll taking place.
      if ('ResizeObserver' in window) {
        new ResizeObserver(this.schedule).observe(document.body);
      }

      this.update();
    }

    /* Reading layout on every scroll event would be wasted work; once per
       frame is as often as the result can be seen. */
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(this.update);
    }

    update() {
      this.frame = 0;
      this.setVisible(window.scrollY > this.threshold);

      // Measured whether the button is shown or not. Dropping the lift the
      // moment it starts to hide would let it fade out on top of the very thing
      // it was keeping clear of.
      this.setLift(this.measureLift());
    }

    setVisible(visible) {
      if (visible === this.visible) return;
      this.visible = visible;

      // Turning the button inert while it has focus would drop focus onto the
      // page body, whatever made it hide. A keyboard user is taken to the start
      // of the page's tab order instead; any other focus is simply let go.
      if (!visible && document.activeElement === this.button) {
        if (this.keyboardFocus) {
          this.moveFocus();
        } else {
          this.button.blur();
        }
      }

      this.button.classList.toggle('is-visible', visible);
      this.button.toggleAttribute('inert', !visible);
      this.button.tabIndex = visible ? 0 : -1;
    }

    /*
      How far the button has to rise so it does not sit on top of anything
      matching the keep-clear selector.

      The vertical edges are read from the painted box with the current lift
      added back. On phones, the area a fixed element is placed in grows as the
      browser toolbar slides away, while the document's own height stays the
      same, so a height taken from the document would put the button in the
      wrong place. The sideways slide used while hidden does not move it up or
      down, so the painted top and bottom stay exact.

      The horizontal edges are read from the layout box instead, because that
      same sideways slide does move the painted box.
    */
    measureLift() {
      if (!this.keepClear) return 0;

      let targets;
      try {
        targets = Array.from(document.querySelectorAll(this.keepClear));
      } catch (error) {
        // A selector typed into the settings that the browser cannot parse.
        return 0;
      }

      // The button is never something to keep clear of. Measuring it, or its
      // icon, would feed its own movement back into the next measurement.
      const rects = targets
        .filter((target) => !this.button.contains(target))
        .map((target) => target.getBoundingClientRect())
        .filter((rect) => rect.width && rect.height);
      if (!rects.length) return 0;

      const painted = this.button.getBoundingClientRect();
      const restingTop = painted.top + this.lift;
      const restingBottom = painted.bottom + this.lift;

      const style = window.getComputedStyle(this.button);
      const width = this.button.offsetWidth;
      const viewportWidth = document.documentElement.clientWidth;
      const left =
        this.position === 'left' ? parseFloat(style.left) : viewportWidth - parseFloat(style.right) - width;
      const right = left + width;

      // Rising above one target can land the button on another stacked just
      // above it, so the check runs again from each new height until nothing is
      // left in the way. The lift only ever grows, so this always settles; one
      // pass per target is the most it can take.
      let lift = 0;
      for (let pass = 0; pass <= rects.length; pass += 1) {
        const top = restingTop - lift;
        const bottom = restingBottom - lift;
        let needed = lift;

        rects.forEach((rect) => {
          if (rect.right <= left || rect.left >= right) return;

          const clearance = rect.top - this.keepClearGap;
          if (clearance >= bottom || rect.bottom <= top) return;

          needed = Math.max(needed, restingBottom - clearance);
        });

        if (needed === lift) break;
        lift = needed;
      }

      // Something too tall to rise above would carry the button off the top of
      // the screen, where it could no longer be seen but could still be reached
      // with the keyboard. It stays where it is instead.
      if (restingTop - lift < 0) return 0;

      return Math.ceil(lift);
    }

    setLift(lift) {
      if (lift === this.lift) return;
      this.lift = lift;
      this.button.style.setProperty('--kv-back-to-top-lift', `${lift}px`);
    }

    /* Focus is not handled here. Wherever the button ends up hiding, whether
       at the end of this scroll, part-way through it or after the visitor
       scrolls up some other way, setVisible deals with focus at that moment. */
    scrollToTop() {
      const start = window.scrollY;
      this.cancelScroll();

      if (start <= 0) return;

      if (reducedMotion.matches || this.duration <= 0) {
        this.jumpTo(0);
        this.update();
        return;
      }

      const began = performance.now();
      this.expectedScroll = start;

      const step = (now) => {
        // Anything else that has moved the page since the last frame, such as
        // dragging the scrollbar, means the visitor has taken over. Some
        // engines send no event at all for that, so the position is compared
        // directly.
        if (Math.abs(window.scrollY - this.expectedScroll) > SCROLL_DRIFT_TOLERANCE) {
          this.cancelScroll();
          return;
        }

        const progress = Math.min(1, (now - began) / this.duration);
        this.expectedScroll = Math.round(start * (1 - swing(progress)));
        this.jumpTo(this.expectedScroll);

        if (progress < 1) {
          this.scrollFrame = requestAnimationFrame(step);
          return;
        }

        this.scrollFrame = 0;
        this.detachInterrupts();
        this.update();
      };

      this.attachInterrupts();
      this.scrollFrame = requestAnimationFrame(step);
    }

    /* Each frame sets the position outright. A smooth scroll-behavior left on
       the root by a stylesheet would turn every one of those steps into an
       animation of its own, so it is switched off for the duration. */
    jumpTo(top) {
      const root = document.documentElement;
      const previous = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, top);
      root.style.scrollBehavior = previous;
    }

    cancelScroll() {
      if (!this.scrollFrame) return;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      this.detachInterrupts();
    }

    attachInterrupts() {
      INTERRUPTING_EVENTS.forEach((type) => window.addEventListener(type, this.cancelScroll, { passive: true }));
    }

    detachInterrupts() {
      INTERRUPTING_EVENTS.forEach((type) => window.removeEventListener(type, this.cancelScroll));
    }

    /* Focus goes to the first thing in the page's tab order, the same place a
       fresh page load starts. It is looked up rather than named, so it keeps
       working whatever that first element happens to be. Anything that cannot
       take focus - hidden, inert, taken out of the tab order, or not rendered at
       all - is passed over. */
    moveFocus() {
      const candidates = document.querySelectorAll(FOCUSABLE);

      for (const candidate of candidates) {
        if (candidate === this.button || candidate.tabIndex < 0) continue;
        if (candidate.closest('[inert], [hidden]')) continue;
        if (!candidate.getClientRects().length) continue;

        candidate.focus({ preventScroll: true });
        if (document.activeElement === candidate) return;
      }
    }
  }

  const start = () => {
    const button = document.querySelector('[data-kv-back-to-top]');
    if (button && !button.kvBackToTop) {
      button.kvBackToTop = new KvBackToTop(button);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
